/** Запросы раздела «Себестоимость» (react-query): ключи и опрос фиксации. */
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import * as pp from "@/api/purchasePrices";

export const KEY = {
  all: ["purchase-prices"] as const,
  prices: (date: string, q: string) => ["purchase-prices", "prices", date, q] as const,
  history: (nom: string, char: string) => ["purchase-prices", "history", nom, char] as const,
  rates: ["purchase-prices", "rates"] as const,
  fixations: ["purchase-prices", "fixations"] as const,
  fixation: (id: number) => ["purchase-prices", "fixation", id] as const,
  missing: (from: string, to: string, legal: string, mp: string, source: string) =>
    ["purchase-prices", "missing", from, to, legal, mp, source] as const,
  cogsFrom: ["purchase-prices", "cogs-from"] as const,
  products: (q: string) => ["purchase-prices", "products", q] as const,
};

/** Опрос фиксации, пока `running`: раз в 4 с (ТЗ: 3–5 с). */
export const POLL_MS = 4_000;

/** Всё, что зависит от записанных цен (список, история, «без цены», фиксации). */
export function invalidatePrices(qc: QueryClient): Promise<void> {
  return qc.invalidateQueries({ queryKey: KEY.all });
}

export const useRates = () => useQuery({ queryKey: KEY.rates, queryFn: pp.getWarehouseRates });

/** Список фиксаций. Пока в нём есть `running`, опрашивается раз в POLL_MS: бэк продвигает идущую
 *  фиксацию на каждом запросе списка, так что прогресс не зависит от открытого экрана фиксации.
 *  Сбой опроса останавливает опрос (ошибку показывает потребитель с кнопкой «Повторить»). */
export const useFixations = () =>
  useQuery({
    queryKey: KEY.fixations,
    queryFn: pp.listFixations,
    refetchInterval: (query) =>
      query.state.status !== "error" && query.state.data?.some((f) => f.status === "running")
        ? POLL_MS
        : false,
  });

/** Фиксация с опросом, пока считается. Сбой опроса (502 — связь с производством) опрос
 *  останавливает и показывается ошибкой с кнопкой «Опросить снова» — не долбим молча. */
export function useFixation(id: number) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: KEY.fixation(id),
    queryFn: () => pp.getFixation(id),
    retry: false,
    refetchInterval: (query) =>
      query.state.status !== "error" && query.state.data?.status === "running" ? POLL_MS : false,
  });
  const status = q.data?.status;
  // фиксация вышла из running — список фиксаций знает старый статус
  useEffect(() => {
    if (status && status !== "running") void qc.invalidateQueries({ queryKey: KEY.fixations });
  }, [status, qc]);
  return q;
}
